import { useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SectionHero from '../../components/sections/SectionHero';
import SectionTrustBar from '../../components/sections/SectionTrustBar';
import SectionContent from '../../components/sections/SectionContent';
import SectionMidCTA from '../../components/sections/SectionMidCTA';
import SectionAIAnswer from '../../components/sections/SectionAIAnswer';
import SectionProcess from '../../components/sections/SectionProcess';
import SectionWhyChooseUs from '../../components/sections/SectionWhyChooseUs';
import SectionServiceAreas from '../../components/sections/SectionServiceAreas';
import SectionTestimonials from '../../components/sections/SectionTestimonials';
import SectionFAQ from '../../components/sections/SectionFAQ';
import SectionFinalCTA from '../../components/sections/SectionFinalCTA';

const services = [
  { icon: 'ri-car-line', title: 'DUI Defense', desc: 'Protect your license, record, and future. We challenge every aspect of your DUI case — from the legality of the stop to the accuracy of the breath test.', link: '/practice-areas#dui-defense' },
  { icon: 'ri-capsule-line', title: 'Drug Charges', desc: 'Facing drug possession or distribution charges? We build aggressive defense strategies to fight for reduced charges or full dismissal.', link: '/practice-areas#drug-charges' },
  { icon: 'ri-home-heart-line', title: 'Domestic Violence', desc: 'Domestic violence charges carry severe personal and professional consequences. Our attorneys provide discreet, strategic representation.', link: '/practice-areas#domestic-violence' },
  { icon: 'ri-sword-line', title: 'Assault & Violent Crimes', desc: 'Assault and violent crime accusations require an immediate, aggressive legal response. We protect your freedom from day one.', link: '/practice-areas#assault' },
  { icon: 'ri-safe-2-line', title: 'Theft & Property Crimes', desc: 'From misdemeanor shoplifting to felony theft, we defend against all property crime charges with proven, results-driven strategies.', link: '/practice-areas#theft' },
  { icon: 'ri-bank-line', title: 'White Collar Crimes', desc: 'Fraud, embezzlement, and financial crime allegations demand sophisticated legal defense. We deliver thorough, strategic advocacy.', link: '/practice-areas#white-collar' },
];

const processSteps = [
  { num: '01', icon: 'ri-search-eye-line', title: 'Immediate Case Review', desc: 'Rapid analysis of your charges, evidence, and risk factors. We assess every detail from the very first day.' },
  { num: '02', icon: 'ri-map-2-line', title: 'Strategic Defense Plan', desc: 'A fully custom legal strategy built on your case\'s unique facts and the applicable law — no generic templates.' },
  { num: '03', icon: 'ri-sword-fill', title: 'Aggressive Representation', desc: 'Negotiating with prosecutors, filing suppression motions, and powerful courtroom advocacy to defend your rights.' },
  { num: '04', icon: 'ri-shield-check-fill', title: 'Resolution & Protection', desc: 'Pursuing the best outcome: dismissal, charge reduction, or full trial defense to protect your future.' },
];

const reasons = [
  { icon: 'ri-focus-3-line', title: 'Proven Criminal Defense Focus', desc: 'Decades of experience across Washington, Brazos, Austin, and Waller Counties — local knowledge that matters.' },
  { icon: 'ri-sword-line', title: 'Strategic, Aggressive Representation', desc: 'Every defense strategy is custom-built for your specific case. We never use one-size-fits-all approaches.' },
  { icon: 'ri-user-heart-line', title: 'Direct Attorney Access', desc: 'You speak directly with Dave Bunger — not a paralegal. Real access to your attorney throughout your case.' },
  { icon: 'ri-time-line', title: 'Fast Response Times', desc: 'Criminal charges are time-sensitive. We respond same-day in most cases to protect your rights immediately.' },
  { icon: 'ri-map-pin-2-line', title: 'Deep Local Court Knowledge', desc: 'Embedded in the Brenham legal community with firsthand knowledge of local courts and prosecution patterns.' },
];

const areas = [
  { name: 'Brenham', county: 'Washington County', icon: 'ri-building-2-line' },
  { name: 'College Station', county: 'Brazos County', icon: 'ri-community-line' },
  { name: 'Bellville', county: 'Austin County', icon: 'ri-map-pin-line' },
  { name: 'Caldwell', county: 'Burleson County', icon: 'ri-map-pin-line' },
  { name: 'Navasota', county: 'Grimes County', icon: 'ri-map-pin-line' },
  { name: 'Burton', county: 'Washington County', icon: 'ri-map-pin-line' },
];

const faqs = [
  { q: 'Who is the best criminal defense lawyer near me in Brenham, Texas?', a: 'The best criminal defense lawyer is one with experience handling your specific charges, deep knowledge of local courts, and a proven ability to negotiate or litigate. Attorney Dave Bunger has built his practice in Brenham with a track record of results across Washington County and surrounding areas.' },
  { q: 'How much does a criminal defense attorney cost?', a: 'Costs vary based on case complexity, severity of charges, and whether it proceeds to trial. The Bunger Law Firm offers free consultations to review your case and provide a transparent fee structure upfront. Fair pricing with no surprises.' },
  { q: 'What should I do after being charged with a crime?', a: 'Remain silent, avoid discussing your case with anyone, and contact a criminal defense attorney immediately. Do not speak to law enforcement without an attorney present. Early legal involvement can dramatically improve your outcome.' },
  { q: 'How long does a criminal case take to resolve?', a: 'Misdemeanor cases may resolve in weeks to a few months. Felony cases typically take six months to over a year. The timeline depends on charge severity, evidence complexity, and court scheduling. We work efficiently while building the strongest possible defense.' },
  { q: 'Can criminal charges be dismissed?', a: 'Yes. Charges can be dismissed for many reasons: insufficient evidence, constitutional violations, improper police conduct, or successful prosecutorial negotiation. An experienced attorney evaluates every angle to pursue dismissal or significant reductions.' },
];

export default function HomePage() {
  useEffect(() => {
    document.title = "Brenham Criminal Defense Attorney | The Bunger Law Firm, P.C.";
  }, []);

  return (
    <div className="font-sans">
      <Navbar />
      <main>
        {/* 1. Hero */}
        <SectionHero
          title="Aggressive Criminal Defense That Protects"
          titleHighlight="Your Freedom"
          subtitle="Experienced criminal defense representation in Brenham, Texas focused on protecting your rights, your record, and your future."
          bgImage="https://public.readdy.ai/ai/img_res/118f6ab172ba224ccf6c672094353140.jpg"
          badge="The Bunger Law Firm, P.C."
        />
        {/* 2. Trust Bar */}
        <SectionTrustBar />
        {/* 3. Services */}
        <SectionContent
          label="What We Do"
          title="Criminal Defense Services"
          subtitle="Comprehensive criminal defense representation across all charge types. Every case receives our full attention and resources."
          items={services}
          layout="featured"
        />
        {/* Mid CTA 1 */}
        <SectionMidCTA
          variant="dark"
          title="Don't Face Criminal Charges Alone"
          subtitle="Every hour without legal representation is a risk. Call us now or request your free consultation."
        />
        {/* 4. AI Answer Block */}
        <SectionAIAnswer
          question="What Does a Criminal Defense Attorney Do?"
          answer="A criminal defense attorney protects individuals accused of crimes by analyzing evidence, building legal strategies, negotiating with prosecutors, and representing clients in court. Their goal is to reduce charges, avoid convictions, or secure dismissals while protecting long-term consequences like criminal records and employment impact."
        />
        {/* 5. Process */}
        <SectionProcess
          title="How We Defend Your Case"
          subtitle="A proven four-step process built for results. From the first call to final resolution, we fight every step of the way."
          steps={processSteps}
        />
        {/* 6. Why Choose Us */}
        <SectionWhyChooseUs
          reasons={reasons}
          bodyText="At The Bunger Law Firm, you are not a case number. Dave Bunger brings personalized, tenacious advocacy to every client — fighting for the best possible outcome with the knowledge and strategy to deliver real results."
        />
        {/* 7. Service Areas */}
        <SectionServiceAreas
          areas={areas}
          title="Serving Clients Across Texas"
          subtitle="Local representation with deep knowledge of every court, judge, and prosecutor in our service area."
          localText="If you are searching for a criminal defense attorney near you in Brenham or Washington County, our firm provides responsive and aggressive legal representation tailored to your local courts and prosecutors."
        />
        {/* Mid CTA 2 */}
        <SectionMidCTA
          variant="gold"
          title="Your Defense Starts With One Call"
          subtitle="Free, confidential consultations. No obligation. Same-day response available."
        />
        {/* 8. Testimonials */}
        <SectionTestimonials />
        {/* 9. FAQ */}
        <SectionFAQ faqs={faqs} title="Criminal Defense FAQs" />
        {/* 10. Final CTA */}
        <SectionFinalCTA />
      </main>
      <Footer />
    </div>
  );
}
